import {
  MICROSOFT365_FEATURE_FIELDS,
  MICROSOFT365_FEATURE_REQUIRES,
  type Microsoft365AdminDto,
  type Microsoft365Feature,
  type Microsoft365SettingsBody,
  type Microsoft365SetupBody,
  type Microsoft365SignInMethod,
  type Microsoft365TeamAdminDto,
  type Microsoft365TeamDto,
  type Microsoft365TestCode,
} from '@dbrain/shared';

/**
 * Yönetim › Entegrasyonlar › Microsoft 365 without the DOM (MsYonetimEntegrasyon, MsYonetimKurulum,
 * MsEkipYoneticisi, MsDenetim): the state the cards show, the setup's steps and fields, the masked app id, the
 * secret's end date and the team switches. Texts are i18n keys under `microsoft365.admin`.
 */

/** "Olay: Microsoft 365" in Denetim kaydı: every `microsoft.*` action (the API matches a left prefix). */
export const MICROSOFT_AUDIT_PREFIX = 'microsoft.';

export const SUMMARY_PATH = '/admin/integrations/microsoft-365';
export const SETUP_PATH = '/admin/integrations/microsoft-365/setup';

// The firm's setup ---------------------------------------------------------------------------------

/**
 * not_set_up: nothing saved yet ("Kur"); incomplete: saved but not usable (a direct sign-in without a secret);
 * off: set up and switched off for the firm; failing: on, but the last test failed; working: on.
 */
export type SetupStatus = 'not_set_up' | 'incomplete' | 'off' | 'failing' | 'working';

export const isSaved = (dto: Pick<Microsoft365AdminDto, 'entraTenantId' | 'clientId'> | undefined): boolean => Boolean(dto?.entraTenantId && dto.clientId);

export function setupStatus(dto: Microsoft365AdminDto): SetupStatus {
  if (!isSaved(dto)) return 'not_set_up';
  if (!dto.configured) return 'incomplete';
  if (!dto.enabled) return 'off';
  if (dto.lastTest && !dto.lastTest.ok) return 'failing';
  return 'working';
}

/** The pill's colour: the Bağlantılar cards' own tones (`.connector-state--*`); '' is the quiet grey. */
export const STATUS_TONE: Record<SetupStatus, '' | 'ready' | 'account' | 'down'> = {
  not_set_up: '',
  incomplete: 'account',
  off: '',
  failing: 'down',
  working: 'ready',
};

/** "Outlook mail ve takvim": what the firm has on (`products.<key>`). */
export function productsKey(features: Record<Microsoft365Feature, boolean>): 'both' | 'mail' | 'calendar' | 'none' {
  if (features.mail_read && features.calendar_read) return 'both';
  if (features.mail_read) return 'mail';
  if (features.calendar_read) return 'calendar';
  return 'none';
}

/**
 * A team admin's card (MsEkipYoneticisi): "Outlook mail ve takvim, OneDrive, SharePoint" (`products.<key>` each), and
 * "yalnız okuma" only while the firm has neither sending nor meetings on.
 */
export function teamProducts(features: Record<Microsoft365Feature, boolean>): { keys: ('both' | 'mail' | 'calendar' | 'none' | 'onedrive' | 'sharepoint')[]; readOnly: boolean } {
  const outlook = productsKey(features);
  const files = [...(features.onedrive_import ? (['onedrive'] as const) : []), ...(features.sharepoint_import ? (['sharepoint'] as const) : [])];
  return { keys: outlook === 'none' && files.length > 0 ? files : [outlook, ...files], readOnly: !features.mail_send && !features.calendar_write };
}

// "Neler açık" (MsYonetimEntegrasyon) -------------------------------------------------------------------

/** The rows' i18n keys (`summary.features.<key>`) and test ids (`m365-feature-<testId>`). */
const FEATURE_ROWS: { feature: Microsoft365Feature; key: string; testId: string; kind: 'read' | 'write' | 'documents' }[] = [
  { feature: 'mail_read', key: 'mail', testId: 'mail', kind: 'read' },
  { feature: 'mail_send', key: 'mailSend', testId: 'mail-send', kind: 'write' },
  { feature: 'calendar_read', key: 'calendar', testId: 'calendar', kind: 'read' },
  { feature: 'calendar_write', key: 'calendarWrite', testId: 'calendar-write', kind: 'write' },
  { feature: 'onedrive_import', key: 'onedrive', testId: 'onedrive', kind: 'documents' },
  { feature: 'sharepoint_import', key: 'sharepoint', testId: 'sharepoint', kind: 'documents' },
];

export interface FeatureRowView {
  feature: Microsoft365Feature;
  key: string;
  testId: string;
  kind: 'read' | 'write' | 'documents';
  on: boolean;
  /** the read switch this one needs is off: the switch is disabled and says so (`features.needs.<feature>`) */
  needs: Microsoft365Feature | null;
}

/** The six rows in the mockup's order: mail reading and sending, calendar reading and meetings, OneDrive, SharePoint. */
export function featureRows(features: Record<Microsoft365Feature, boolean>): FeatureRowView[] {
  return FEATURE_ROWS.map((row) => {
    const required = MICROSOFT365_FEATURE_REQUIRES[row.feature];
    return { ...row, on: features[row.feature], needs: required && !features[required] ? required : null };
  });
}

/**
 * The PATCH body of one switch. Turning a read switch off turns its write switch off in the same change (the
 * database refuses a write switch without its read one; each change is audited); a write switch never goes on
 * without its read switch.
 */
export function featureChange(feature: Microsoft365Feature, on: boolean, features: Record<Microsoft365Feature, boolean>): Microsoft365SettingsBody | null {
  const required = MICROSOFT365_FEATURE_REQUIRES[feature];
  if (on && required && !features[required]) return null;
  const body: Record<string, boolean> = { [MICROSOFT365_FEATURE_FIELDS[feature]]: on };
  if (!on) {
    for (const [dependent, needs] of Object.entries(MICROSOFT365_FEATURE_REQUIRES) as [Microsoft365Feature, Microsoft365Feature][]) {
      if (needs === feature && features[dependent]) body[MICROSOFT365_FEATURE_FIELDS[dependent]] = false;
    }
  }
  return body as Microsoft365SettingsBody;
}

/** "Alınan belgeler" under "Kim kullanabilir": only while one of the document switches is on. */
export const documentsOn = (features: Record<Microsoft365Feature, boolean>): boolean => features.onedrive_import || features.sharepoint_import;

/** Sending or meetings is on: the summary's note says how they ask instead of "hiçbir şey göndermez". */
export const writesOn = (features: Record<Microsoft365Feature, boolean>): boolean => features.mail_send || features.calendar_write;

/** "a8d0e4b2-…-19c6": enough to tell two registrations apart; the summary never shows the whole id. */
export function maskId(id: string | null | undefined): string | null {
  const clean = id?.trim();
  if (!clean) return null;
  return clean.length <= 13 ? clean : `${clean.slice(0, 8)}-…-${clean.slice(-4)}`;
}

export type SecretExpiry = { kind: 'none' } | { kind: 'ok' | 'soon' | 'expired'; days: number };

/** The secret's end date against the company's today (YYYY-MM-DD): soon within 30 days, expired after the day. */
export function secretExpiry(expiresOn: string | null | undefined, today: string): SecretExpiry {
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if (!expiresOn || !day.test(expiresOn) || !day.test(today)) return { kind: 'none' };
  const days = Math.round((Date.parse(`${expiresOn}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);
  return { kind: days < 0 ? 'expired' : days <= 30 ? 'soon' : 'ok', days };
}

/** "30 Eki 2027": a calendar date as written, never shifted by a time zone. */
export function dateLabel(ymd: string, locale: string): string {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// The setup's steps ----------------------------------------------------------------------------------

export const SETUP_STEPS = [1, 2, 3, 4] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];

/** The step in the address (?step=); consent and the test open once the registration is saved. */
export function stepOf(param: string | null, dto: Microsoft365AdminDto | undefined): SetupStep {
  const wanted = (SETUP_STEPS as readonly number[]).includes(Number(param)) ? (Number(param) as SetupStep) : 1;
  return wanted > 2 && !isSaved(dto) ? 2 : wanted;
}

/** The steps a click may open: the first two always, the others once the registration is saved. */
export const stepOpen = (step: SetupStep, dto: Microsoft365AdminDto | undefined): boolean => step <= 2 || isSaved(dto);

/** A GUID as Microsoft shows it (8-4-4-4-12 hexadecimal digits); the server stores it in lower case. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isGuid = (value: string): boolean => GUID.test(value.trim());

/** Step 2's fields. The secret is what was typed now: empty keeps the saved one, and it is never filled in. */
export interface SetupForm {
  entraTenantId: string;
  clientId: string;
  clientSecret: string;
  /** YYYY-MM-DD or empty */
  secretExpiresOn: string;
  signInMethod: Microsoft365SignInMethod;
}

export function formOf(dto: Microsoft365AdminDto | undefined): SetupForm {
  return {
    entraTenantId: dto?.entraTenantId ?? '',
    clientId: dto?.clientId ?? '',
    clientSecret: '',
    secretExpiresOn: dto?.secretExpiresOn ?? '',
    signInMethod: dto?.signInMethod ?? 'auth_code',
  };
}

export type SetupProblem = 'entraTenantId' | 'clientId' | 'clientSecret';

/** What keeps step 2 from saving: two GUIDs and, for a direct sign-in, a secret (saved or typed). */
export function setupProblems(form: SetupForm, hasSecret: boolean): SetupProblem[] {
  const problems: SetupProblem[] = [];
  if (!isGuid(form.entraTenantId)) problems.push('entraTenantId');
  if (!isGuid(form.clientId)) problems.push('clientId');
  if (form.signInMethod === 'auth_code' && !hasSecret && !form.clientSecret.trim()) problems.push('clientSecret');
  return problems;
}

/** The PUT body: the secret only when typed (write-only), no date as null. */
export function setupBody(form: SetupForm): Microsoft365SetupBody {
  const secret = form.clientSecret.trim();
  return {
    entraTenantId: form.entraTenantId.trim().toLowerCase(),
    clientId: form.clientId.trim().toLowerCase(),
    signInMethod: form.signInMethod,
    secretExpiresOn: form.secretExpiresOn || null,
    ...(secret ? { clientSecret: secret } : {}),
  };
}

/** Nothing to save: the same registration, method and date, and no new secret. */
export function setupUnchanged(dto: Microsoft365AdminDto, form: SetupForm): boolean {
  const body = setupBody(form);
  return (
    isSaved(dto) &&
    body.clientSecret === undefined &&
    body.entraTenantId === dto.entraTenantId &&
    body.clientId === dto.clientId &&
    body.signInMethod === dto.signInMethod &&
    (body.secretExpiresOn ?? null) === dto.secretExpiresOn
  );
}

/** Another tenant or app: everyone connected to the old one loses their connection (the API removes them). */
export function resetsAccounts(dto: Microsoft365AdminDto, form: SetupForm): boolean {
  if (!isSaved(dto) || dto.connectedCount === 0) return false;
  const body = setupBody(form);
  return body.entraTenantId !== dto.entraTenantId || body.clientId !== dto.clientId;
}

// The test ------------------------------------------------------------------------------------------

const TEST_CODES: readonly Microsoft365TestCode[] = [
  'tenant_not_found',
  'app_not_found',
  'bad_secret',
  'secret_expired',
  'no_secret',
  'public_client_disabled',
  'redirect_not_https',
  'unreachable',
  'unexpected',
];

/** `testCodes.<code>`: a known reason, anything else as "unexpected" (never the raw code). */
export const testCodeOf = (code: string | null | undefined): Microsoft365TestCode =>
  (TEST_CODES as readonly string[]).includes(code ?? '') ? (code as Microsoft365TestCode) : 'unexpected';

/** Where a failed test is put right: the registration's fields (2), Microsoft's side (1), or nowhere here. */
export const TEST_FIX_STEP: Record<Microsoft365TestCode, SetupStep | null> = {
  tenant_not_found: 2,
  app_not_found: 2,
  bad_secret: 2,
  secret_expired: 2,
  no_secret: 2,
  public_client_disabled: 1,
  redirect_not_https: 2,
  unreachable: null,
  unexpected: null,
};

// Teams ---------------------------------------------------------------------------------------------

/** "Kim kullanabilir": the teams the super admin chose (with availability 'teams'). */
export const allowedTeams = (teams: Microsoft365TeamDto[]): Microsoft365TeamDto[] => teams.filter((team) => team.allowed);

/** The teams whose admin turned it off ("Pazarlama ekibi kapattı"), by name. */
export const teamsTurnedOff = (teams: Microsoft365TeamDto[]): string[] => teams.filter((team) => !team.teamEnabled).map((team) => team.name);

/** Why a team admin cannot use their switch now (`team.blocked.<reason>`), or null when they can. */
export function teamBlock(dto: Microsoft365TeamAdminDto, team: Microsoft365TeamDto): 'notSetUp' | 'firmOff' | 'notAllowed' | null {
  if (!dto.configured) return 'notSetUp';
  if (!dto.enabled) return 'firmOff';
  if (dto.availability === 'teams' && !team.allowed) return 'notAllowed';
  return null;
}

/** "Kim kullanabilir" as saved: did the dialog change anything? */
export function audienceChanged(dto: Microsoft365AdminDto, availability: Microsoft365AdminDto['availability'], chosen: ReadonlySet<string>): boolean {
  if (availability !== dto.availability) return true;
  if (availability === 'everyone') return false;
  const before = new Set(allowedTeams(dto.teams).map((team) => team.id));
  return before.size !== chosen.size || [...chosen].some((id) => !before.has(id));
}

/** "A, B ve C" in the language's own way; past `max` names the rest is counted ("… ve 3 ekip daha"). */
export function nameList(names: string[], locale: string, more: (count: number) => string, max = 4): string {
  const shown = names.length > max ? [...names.slice(0, max - 1), more(names.length - (max - 1))] : names;
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(shown);
}

// Denetim kaydı --------------------------------------------------------------------------------------

/** The "Olay" filter for an `action` parameter: Microsoft 365, other chosen events (a deep link), or none. */
export function eventFilterOf(action: string | null | undefined): 'microsoft' | 'chosen' | null {
  if (!action) return null;
  return action === MICROSOFT_AUDIT_PREFIX ? 'microsoft' : 'chosen';
}

/** Microsoft's answer to admin consent, from the summary's address (?consent=granted|denied). */
export function consentOf(value: string | null): 'granted' | 'denied' | null {
  return value === 'granted' || value === 'denied' ? value : null;
}
